import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Header } from "semantic-ui-react";

import Cookies from "js-cookie";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import ModInfo from "react-lib/apps/common/ModInfo";
import SnackMessage from "react-lib/apps/common/SnackMessage";

import ModConfirmOA from "./ModConfirmOA";
import ModInfoOA from "./ModInfoOA";
import UserProfileFormBasic, { UserProfileBasicType } from "./UserProfileFormBasic";
import UserProfileFormDetailed, { UserProfileDetailType } from "./UserProfileFormDetailed";
import UserProfileImage from "./UserProfileImage";

import { adToBeWithSetFormat, beToAd, formatADtoBEString } from "react-lib/utils/dateUtils";
import { updateEmail, getAuth, signInWithCustomToken } from "firebase/auth";
import { FirebaseError, getApp } from "firebase/app";
import { Functions, httpsCallable } from "firebase/functions";
import { EducationDataType, ExperienceDataType } from "../../HISV3/HRM/sequence/EmployeeInfo";
import { convertUrlToFile } from "../../HISV3/RHB/RHBInterface";

type UserData = UserProfileBasicType & UserProfileDetailType;

type UserProfileProps = {
  controller?: any;
  functionsWithRegion?: any;
  data?: UserData | null;
  emailLogin?: string;
  facebookName?: string;
  isEmailLogin?: boolean;
  isFacebookLogin?: boolean;
  isGoogleLogin?: boolean;
  isLineLogin?: boolean;
  layout?: "basic" | "detailed";
  lineId?: string;
  myDivisions?: Record<string, any>[];
  readOnly?: boolean;
  userId?: number;
  onCreateOA?: () => void;
  onGetDivision?: () => Promise<void>;
  onRefreshUserInfo?: (response: any) => void;
  buttonLoadCheck?: Record<string, any>;
  selectedDivision?: Record<string, any>;
  divisionHasUserItems?: Record<string, any>[];
  languageUX?: "th" | "en";
};

const PROFILE_IMAGE_URL = `${process.env.PUBLIC_URL}/static/images/person-placeholder.png`;

const UserProfile = (props: UserProfileProps) => {
  const intl = useIntl();
  const history = useHistory();

  const isMounted = useRef(true);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const [userData, setUserData] = useState<UserData>({});

  const [positionOptions, setPositionOptions] = useState<any[]>([]);
  const [specialtyOptions, setSpecialtyOptions] = useState<any[]>([]);
  const [eduCategoryOptions, setEduCategoryOptions] = useState<any[]>([]);
  const [fileImage, setFileImage] = useState<File | null>(null);
  const [openModInfo, setOpenModInfo] = useState(false);
  const [modInfoMsg, setModInfoMsg] = useState("");
  const [openModConfirm, setOpenModConfirm] = useState(false);
  const [isInfoComplete, setIsInfoComplete] = useState<boolean>(false);
  const [isProvider, setIsProvider] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<any>(null);

  const checkInfoComplete = useCallback(
    (data: UserData) =>
      !!data.first_name &&
      !!data.last_name &&
      !!data.first_name_en &&
      !!data.last_name_en &&
      !!data.dob &&
      !!data.email &&
      !!data.phone_no,
    []
  );

  useEffect(() => {
    const divisionHasUserData = (props.divisionHasUserItems || []).find(
      (item: any) => item.division?.id === props.selectedDivision?.id
    );

    setIsProvider(divisionHasUserData?.is_doctor === true);
  }, [props.divisionHasUserItems, props.selectedDivision]);

  useEffect(() => {
    const getPositionOptions = async ({ apiToken }: any) => {
      const [data] = await props.controller.getUserPosition({
        apiToken,
      });

      if (!data?.items) {
        return;
      }

      const items: Record<string, any>[] = data?.items ?? [];

      const prepare = items
        .filter((item) => item.is_active)
        .filter((item) => item.code)
        .filter((item) => item.name)
        .map((item) => {
          const option: Record<string, any> = {};

          option.key = item.id;
          option.value = item.code;
          option.text = item.name;

          return option;
        });

      setPositionOptions(prepare);
      setUserData((prev) => ({ ...prev, position: prepare[0].value }));
    };

    if (Cookies.get("apiToken") && props.controller?.getUserPosition) {
      void getPositionOptions({ apiToken: Cookies.get("apiToken") });
    }

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const getSpecialtyOptions = async ({ apiToken }: any) => {
      const [data] = await props.controller.getSpecialty({
        apiToken,
      });

      if (!data?.items) {
        return;
      }

      const items: Record<string, any>[] = data?.items ?? [];

      const prepare = items
        .filter((item) => item.is_active)
        .filter((item) => item.code)
        .filter((item) => item.name)
        .map((item) => {
          const option: Record<string, any> = {};

          option.key = item.id;
          option.value = item.id;
          option.text = item.name;

          return option;
        });

      setSpecialtyOptions(prepare);
    };

    if (Cookies.get("apiToken") && props.controller?.getUserPosition) {
      void getSpecialtyOptions({ apiToken: Cookies.get("apiToken") });
    }

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const getEduCategoryOptions = async ({ apiToken }: any) => {
      const [data] = await props.controller.getEDUCategory({
        apiToken,
      });

      if (!data?.edu_category?.items) {
        return;
      }

      const items: Record<string, any>[] = data?.edu_category?.items ?? [];

      setEduCategoryOptions(items);
    };

    if (Cookies.get("apiToken") && props.controller?.getUserPosition) {
      void getEduCategoryOptions({ apiToken: Cookies.get("apiToken") });
    }

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const getProfile = async ({ apiToken }: any) => {
      if (props.data) {
        setUserData({ ...props.data, birthdate: formatADtoBEString(props.data.dob) });

        return;
      }

      const promiseArr = [
        props.controller.getUserProfile({
          apiToken,
        }),
        props.controller.getOAUserProfile({
          apiToken,
        }),
      ];

      if (props.controller.getUserAccount && props.userId) {
        promiseArr.push(props.controller.getUserEmployee({ id: props.userId }));
      }

      const [[profile], [profileOA], [employee]] = await Promise.all(promiseArr);

      if (profile) {
        props.controller.getUserEmployee({
          id: profile.user,
          apiToken,
        });

        const [providerRes, providerErr, providerNet] =
          await props.controller.getDoctorProviderInfo({
            userId: profile?.user,
            apiToken,
          });

        const providerData = providerRes?.doctor_info;

        setUserData((prev) => ({
          ...prev,
          cid: profile.cid,
          email: profile.email,
          first_name: profile.first_name,
          first_name_en: profile.first_name_en,
          last_name: profile.last_name,
          last_name_en: profile.last_name_en,
          phone_no: profile.phone_no,
          user: profile.user,
          dob: profile.dob,
          // Employment Information
          doctor_id: providerData?.id,
          doctor_first_name: providerData?.first_name,
          doctor_last_name: providerData?.last_name,
          certificate_no: providerData?.certificate_no,
          doctor_code: providerData?.code,
          specialties: providerData?.specialties,
          flag_start_date: adToBeWithSetFormat(employee?.flag_start_date),
          flag_end_date: adToBeWithSetFormat(employee?.flag_end_date),
          inactive_reason: employee?.inactive_reason,
        }));
      }

      if (profileOA) {
        setUserData((prev) => ({
          ...prev,
          licence: profileOA.licence,
          position: profileOA.position,
        }));
      }

      if (employee) {
        const [[education], [experience]] = await Promise.all([
          props.controller.getEmployeeEducation({
            employeeId: employee?.id,
            apiToken,
          }),
          props.controller.getEmployeeExperience({
            employeeId: employee?.id,
            apiToken,
          }),
        ]);

        setUserData((prev) => ({
          ...prev,
          cid: employee.citizen_no,
          code: employee.code,
          status: employee.status,
          employeeId: employee.id,
          education: education.items,
          experience: experience.items,
        }));
      }

      const image = profileOA?.image || profile?.image_url || employee?.image;

      setUserData((prev) => ({ ...prev, image }));
      setIsInfoComplete(checkInfoComplete({ ...profile, ...profileOA }));

      if (profile?.dob) {
        setUserData((prev) => ({ ...prev, birthdate: formatADtoBEString(profile.dob) }));
      }
    };

    if (Cookies.get("apiToken") && props.controller?.getUserProfile) {
      void getProfile({ apiToken: Cookies.get("apiToken") });
    }
  }, []);

  useEffect(() => {
    void props.onGetDivision?.();
  }, []);

  // Login info as useMemo
  const loginInfo = useMemo(() => {
    const isJustLogin =
      !!props.isLineLogin ||
      !!props.isFacebookLogin ||
      !!props.isEmailLogin ||
      !!props.isGoogleLogin;

    let loginDisplay = {
      label: "",
      method: "",
      value: "",
    };

    if (props.isLineLogin) {
      loginDisplay = {
        label: "Line ID",
        method: "LINE",
        value: props.lineId ?? "",
      };
    } else if (props.isFacebookLogin) {
      loginDisplay = {
        label: "Facebook Name",
        method: "Facebook",
        value: props.facebookName ?? "",
      };
    } else if (props.isEmailLogin) {
      loginDisplay = {
        label: "Email:",
        method: "Email",
        value: props.emailLogin ?? "",
      };
    } else if (props.isGoogleLogin) {
      loginDisplay = {
        label: "Google:",
        method: "Google",
        value: "",
      };
    }

    return {
      emailLogin: props.emailLogin,
      facebookName: props.facebookName,
      isEmailLogin: props.isEmailLogin,
      isFacebookLogin: props.isFacebookLogin,
      isJustLogin,
      isLineLogin: props.isLineLogin,
      lineId: props.lineId,
      loginDisplay,
    };
  }, [
    props.emailLogin,
    props.facebookName,
    props.isEmailLogin,
    props.isFacebookLogin,
    props.isLineLogin,
    props.lineId,
  ]);

  async function reauthWithCustomToken(functions: Functions) {
    const auth = getAuth();

    const uid = auth.currentUser!.uid;
    const createToken = httpsCallable(functions, "createTokenFromUser"); // your callable that returns { token }
    const { data } = await createToken(uid);
    await signInWithCustomToken(auth, data as string); // now “recent login”
  }

  const handleSave = async () => {
    const {
      birthdate,
      cid,
      code,
      email,
      first_name: firstName,
      first_name_en: firstNameEn,
      last_name: lastName,
      last_name_en: lastNameEn,
      licence,
      phone_no: phone,
      position,
      status,
      user,
      flag_end_date,
      flag_start_date,
    } = userData;

    const fieldChecks = [
      [firstName, "ชื่อ"],
      [lastName, "นามสกุล"],
      [birthdate, "วันเกิด"],
    ];

    if (props.layout === "detailed") {
      fieldChecks.push(
        [email, "Email"],
        [phone, "เบอร์โทร"],
        [firstNameEn, "Firstname"],
        [lastNameEn, "Lastname"]
      );
    } else {
      fieldChecks.push([position, "ตำแหน่ง"], [licence, "เลขที่ใบประกอบ"]);
    }

    const missingFields = fieldChecks.filter(([field]) => !field).map(([, name]) => name);

    if (missingFields.length > 0) {
      alert(`กรุณาระบุฟิลด์จำเป็น ${missingFields.join(" ")}`);

      return;
    }

    setIsSaveLoading(true);

    const positionCode = position || "TEMP";
    const positionId = positionOptions.find((option) => option.value === positionCode)?.key;
    const specialtyId = specialtyOptions.find((option) => option.value === positionCode)?.key;

    const apiToken = Cookies.get("apiToken");

    const data = {
      apiToken,
      cid,
      code: code || `ISH-${user}`,
      dob: birthdate,
      email,
      firstName,
      firstNameEn,
      image: fileImage,
      lastName,
      lastNameEn,
      licence,
      phone,
      position: positionCode,
      positionId,
      specialtyId,
      status: status || "ACTIVE",
      user,
      flag_end_date: beToAd(flag_end_date || "")?.format("YYYY-MM-DD") || "",
      flag_start_date: beToAd(flag_start_date || "")?.format("YYYY-MM-DD") || "",
    };

    if (props.controller.updateUserEmployee) {
      // ไม่อัพรูปสำหรับ staff
      const { image, ...dataWithoutImage } = data;

      const [, employeeError] = await props.controller.updateUserEmployee(dataWithoutImage);

      if (employeeError) {
        setErrorMessage(employeeError);
        setIsSaveLoading(false);

        return;
      }

      let auth = getAuth();
      // Update Email to firebase
      console.log("auth: ", auth);
      if (auth?.currentUser && email) {
        updateEmail(auth?.currentUser, email)
          .then(() => {
            console.log("Email updated successfully");
          })
          .catch(async (error) => {
            if (error instanceof FirebaseError) {
              switch (error.code) {
                case "auth/requires-recent-login":
                  await reauthWithCustomToken(props.functionsWithRegion);
                  auth = getAuth();
                  if (auth.currentUser !== null) {
                    updateEmail(auth.currentUser, email)
                      .then(() => {
                        console.log("Email updated successfully");
                      })
                      .catch((error) => {
                        console.log("error: ", error);
                      });
                  }
                  break;
                case "auth/operation-not-allowed":
                default:
                  console.log("error: ", error);
              }
            }
          });
      }
    }

    const doctorData = {
      certificate_no: userData?.certificate_no,
      doctor_code: userData?.doctor_code,
      specialties: userData?.specialties,
      first_name: userData?.doctor_first_name,
      last_name: userData?.doctor_last_name,
      code: userData?.doctor_code,
      is_active: true,
    };

    const formDoctorData = new FormData();

    for (const [key, value] of Object.entries(doctorData)) {
      if (value === undefined || value === null) continue;

      if (Array.isArray(value)) {
        value.forEach((v) => {
          formDoctorData.append(key, String(v));
        });
      } else {
        formDoctorData.append(key, String(value));
      }
    }

    let imageFile: File | null = null;

    if (data.image instanceof File) {
      // มีการ upload รูปภาพใหม่
      imageFile = data.image;
    } else if (userData?.image) {
      // ไม่มีการ upload รูปภาพ หมอจะใช้รูปภาพเดิมจาก profile
      imageFile = await convertUrlToFile(userData.image);
    }

    if (imageFile) {
      formDoctorData.append("image", imageFile);
    }

    const [profileRes, profileError] = await props.controller.updateUserProfile(data)
    const [profileOARes, profileOAError] = await props.controller.patchOAUserProfile(data)

    const handleError = () => {
      setIsSaveLoading(false);
      setModInfoMsg("เกิดข้อผิดพลาด");
    };

    if (profileError || profileOAError) {
      handleError();
      return;
    }

    if (userData?.doctor_id && isProvider) {
      const [, doctorErr] = await props.controller.updateDoctorDetail({
        apiToken,
        doctorData: formDoctorData,
        id: userData.doctor_id,
      });

      if (doctorErr) {
        handleError();
        return;
      }
    }

    setIsSaveLoading(false);
    setModInfoMsg("บันทึกสำเร็จ");
    props.onRefreshUserInfo?.(profileRes);

    setUserData((prev) => ({ ...prev, image: profileOARes?.image || "" }));

    if (props.layout === "detailed" && !isInfoComplete) {
      setOpenModConfirm(true);
      setIsInfoComplete(false);

      return;
    }

    setOpenModInfo(true);

    console.log(" isLineLogin", props.isLineLogin);
    console.log(" isFacebookLogin", props.isFacebookLogin);
    console.log(" isEmailLogin", props.isEmailLogin);

    const isNotLoggedIn = !props.isLineLogin && !props.isFacebookLogin && !props.isEmailLogin;

    // If came from Login Page goto ShowDivision
    if (isNotLoggedIn || props.layout === "detailed") {
      return;
    }

    // push to next page

    // Logic Same Condition##Goto #2
    // API that check request
    const [listDivision] = await props.controller.getListOARequestCheck({
      apiToken: Cookies.get("apiToken"),
    });

    if (listDivision?.items?.length > 0) {
      // props.controller.setState({ messageShowPending: "listDivision" });
      // Goto Waiting if have pending...
      console.log("handleLoginSuccess Goto /ShowDivision  from Login success");
      history.push({ pathname: "/ShowDivision" });

      return;
    }

    console.log("handleLoginSuccess props.myDivisions", props.myDivisions);

    // No Division
    if (
      (props.myDivisions?.length === 1 && props.myDivisions[0].code === "create_oa") ||
      (props.myDivisions ?? []).length === 0
    ) {
      console.log("handleLoginSuccess Goto /CreateOA  from Login success");
      history.push({ pathname: "/CreateOA" });

      return;
    }

    if ((props.myDivisions ?? []).length > 1) {
      console.log("handleLoginSuccess Goto /Chat  from Login success");
      history.push({ pathname: "/Chat" });
    }
  };

  const handleCreateUpdateEmployeeEducationInfo = async (
    educationData: EducationDataType,
    callback?: (result: { success: boolean }) => void
  ) => {
    const employeeId = userData?.employeeId;
    const apiToken = Cookies.get("apiToken");

    const [eduRes, eduErr, eduNet] = await props.controller.createUpdateEmployeeEducationInfo({
      apiToken,
      employeeId,
      educationData,
    });

    if (eduRes) {
      const [education] = await props.controller.getEmployeeEducation({
        employeeId,
        apiToken,
      });

      setUserData((prev) => ({
        ...prev,
        education: education.items,
      }));

      callback?.({ success: true });
    } else {
      setErrorMessage(eduErr);
    }
  };

  const handleDeleteEmployeeEducationInfo = async (id: number) => {
    const employeeId = userData?.employeeId;
    const apiToken = Cookies.get("apiToken");

    const [eduRes, eduErr, eduNet] = await props.controller.deleteEmployeeEducationInfo({
      apiToken,
      employeeId,
      educationId: id,
    });

    if (eduErr) {
      setErrorMessage(eduErr);
    }

    const [education] = await props.controller.getEmployeeEducation({
      employeeId,
      apiToken,
    });

    setUserData((prev) => ({
      ...prev,
      education: education.items,
    }));
  };

  const handleCreateUpdateEmployeeExperienceInfo = async (
    experienceData: ExperienceDataType,
    callback?: (result: { success: boolean }) => void
  ) => {
    const employeeId = userData?.employeeId;
    const apiToken = Cookies.get("apiToken");

    const [eduRes, eduErr, eduNet] = await props.controller.createUpdateEmployeeExperienceInfo({
      apiToken,
      employeeId,
      experienceData,
    });

    if (eduRes) {
      const [experience] = await props.controller.getEmployeeExperience({
        employeeId,
        apiToken,
      });

      setUserData((prev) => ({
        ...prev,
        experience: experience.items,
      }));

      callback?.({ success: true });
    } else {
      setErrorMessage(eduErr);
    }
  };

  const handleDeleteEmployeeExperienceInfo = async (id: number) => {
    const employeeId = userData?.employeeId;
    const apiToken = Cookies.get("apiToken");

    const [eduRes, eduErr, eduNet] = await props.controller.deleteEmployeeExperienceInfo({
      apiToken,
      employeeId,
      experienceId: id,
    });

    if (eduErr) {
      setErrorMessage(eduErr);
    }

    const [experience] = await props.controller.getEmployeeExperience({
      employeeId,
      apiToken,
    });

    setUserData((prev) => ({
      ...prev,
      experience: experience.items,
    }));
  };

  // Handle user data changes
  const handleChangeValue = (e: any, data: any) => {
    setUserData((prev) => ({ ...prev, [data.name]: data.value }));
  };

  const handleImageChange = (file: File, fileUrl: string) => {
    setFileImage(file);

    setUserData((prev) => ({ ...prev, image: fileUrl }));
  };

  const handleClearImage = () => {
    setUserData((prev) => ({ ...prev, image: "" }));
  };

  const handleCloseModConfirm = () => {
    setOpenModConfirm(false);
  };

  return (
    <>
      <SnackMessage
        error={errorMessage}
        success={null}
        onClose={() => {
          setErrorMessage(null);
        }}
      />

      {openModInfo &&
        (props.layout === "detailed" ? (
          <ModInfoOA
            open={!openModConfirm}
            type={modInfoMsg.includes("บันทึกสำเร็จ") ? "success" : "error"}
            onApprove={() => {
              setOpenModInfo(false);
            }}
            onClose={handleCloseModConfirm}
          />
        ) : (
          <ModInfo
            alertText={modInfoMsg}
            btnText={intl.formatMessage({ id: "ตกลง" })}
            open={openModInfo}
            titleColor={modInfoMsg.includes("บันทึกสำเร็จ") ? "green" : "red"}
            onApprove={() => {
              setOpenModInfo(false);
            }}
            titleName={
              modInfoMsg.includes("บันทึกสำเร็จ") ? "บันทึกเรียบร้อย" : "เกิดข้อผิดพลาด !!"
            }
          />
        ))}

      <ModConfirmOA
        approveButtonText="ต้องการสร้าง"
        denyButtonText="ไว้ทีหลัง"
        open={openModConfirm}
        subtitle="ต้องการสร้าง Official Account เลยหรือไม่"
        title="สร้างข้อมูลส่วนตัวสำเร็จ"
        onApprove={props.onCreateOA}
        onClose={handleCloseModConfirm}
        onDeny={handleCloseModConfirm}
      />

      <div className="register-new-user">
        <UserProfileImage
          readOnly={props.readOnly}
          src={userData.image || PROFILE_IMAGE_URL}
          onChange={handleImageChange}
          onClear={handleClearImage}
        />
        <div className="vl" />
        <div
          className="profile-info"
          style={{
            overflowY: "auto",
            marginBottom: "50px",
          }}
        >
          <Header style={{ color: "var(--primary-theme-color)", margin: "1.25rem 0 2rem" }} as="h1">
            ข้อมูลส่วนตัว
          </Header>

          {props.layout === "detailed" ? (
            <UserProfileFormDetailed
              isSaveLoading={isSaveLoading}
              isProvider={isProvider}
              loginInfo={loginInfo}
              readOnly={props.readOnly}
              userData={userData}
              specialtyOptions={specialtyOptions}
              eduCategoryOptions={eduCategoryOptions}
              onChangeValue={handleChangeValue}
              onSave={handleSave}
              onCreateUpdateEmployeeEducationInfo={handleCreateUpdateEmployeeEducationInfo}
              onDeleteEmployeeEducationInfo={handleDeleteEmployeeEducationInfo}
              onCreateUpdateEmployeeExperienceInfo={handleCreateUpdateEmployeeExperienceInfo}
              onDeleteEmployeeExperienceInfo={handleDeleteEmployeeExperienceInfo}
              buttonLoadCheck={props.buttonLoadCheck}
              languageUX={props.languageUX}
            />
          ) : (
            <UserProfileFormBasic
              isSaveLoading={isSaveLoading}
              loginInfo={loginInfo}
              userData={userData}
              positionOptions={positionOptions}
              onChangeValue={handleChangeValue}
              onSave={handleSave}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default UserProfile;
